<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title>
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Add Stock Log
        </v-card-title>
        <v-card-text>
          <v-row>
            <!-- Product Selection -->
            <v-col sm="4">
              <v-autocomplete
                v-model="selectedProduct"
                :items="filteredProducts"
                label="Select Product *"
                outlined
                item-text="name"
                item-value="id"
                prepend-inner-icon="mdi-package"
                :error-messages="validationErrors.product_id"
                @change="onProductChange"
                clearable
                :no-data-text="products.length === 0 ? 'Loading products...' : 'No products available'"
              ></v-autocomplete>
            </v-col>

            <!-- Variant Selection -->
            <v-col sm="4">
              <v-autocomplete
                v-model="form.variant_id"
                :items="productVariants"
                label="Select Product Variant *"
                outlined
                :item-text="formatVariantName"
                item-value="id"
                prepend-inner-icon="mdi-package-variant"
                :error-messages="validationErrors.variant_id"
                :disabled="!selectedProduct"
                @change="onVariantChange"
                clearable
                :no-data-text="!selectedProduct ? 'Select a product first' : 'No variants available'"
              ></v-autocomplete>
            </v-col>

            <!-- Action Type -->
            <v-col sm="4">
              <v-autocomplete
                ref="action_type"
                outlined
                v-model="form.action_type"
                :items="actionTypeItems"
                label="Action Type *"
                prepend-inner-icon="mdi-swap-horizontal"
                clearable
                :error-messages="validationErrors.action_type"
              ></v-autocomplete>
            </v-col>

            <!-- Quantity -->
            <v-col sm="4">
              <v-text-field
                v-model="form.quantity"
                outlined
                label="Quantity *"
                prepend-inner-icon="mdi-numeric"
                type="number"
                :error-messages="validationErrors.quantity"
              ></v-text-field>
            </v-col>

            <!-- Remarks -->
            <v-col sm="8">
              <v-textarea
                v-model="form.remarks"
                outlined
                label="Remarks"
                prepend-inner-icon="mdi-note-text"
                rows="3"
                :error-messages="validationErrors.remarks"
              ></v-textarea>
            </v-col>

            <!-- Is Active Switch -->
            <v-col sm="4">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Selected Variant Details Card -->
          <v-card v-if="selectedVariant" class="mb-5" elevation="2" outlined>
            <v-card-title class="subtitle-1">Selected Variant Information</v-card-title>
            <v-card-text>
              <v-row>
                <v-col sm="3">
                  <strong>Product:</strong> {{ getProductName() }}
                </v-col>
                <v-col sm="3">
                  <strong>Variant:</strong> {{ selectedVariant.color }} - {{ selectedVariant.size }}
                </v-col>
                <v-col sm="3">
                  <strong>Price:</strong> ₹{{ selectedVariant.price || '0.00' }}
                </v-col>
                <v-col sm="3">
                  <strong>Company Stock:</strong>
                  <v-chip small :color="getCompanyStock() > 0 ? 'green' : 'red'" text-color="white">
                    {{ getCompanyStock() }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Stock Preview Card -->
          <v-card v-if="selectedVariant && form.action_type && form.quantity" class="mb-5" elevation="2" outlined :class="getPreviewCardClass()">
            <v-card-title class="subtitle-1">
              Stock Transaction Preview
              <v-spacer></v-spacer>
              <v-icon v-if="isInvalidTransaction()" color="error" small>mdi-alert-circle</v-icon>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col sm="3">
                  <strong>Current Company Stock:</strong>
                  <v-chip small color="blue" text-color="white">
                    {{ getCompanyStock() }}
                  </v-chip>
                </v-col>
                <v-col sm="3">
                  <strong>Transaction:</strong>
                  <v-chip small :color="getTransactionColor()" text-color="white">
                    {{ getTransactionPrefix() }}{{ form.quantity }}
                  </v-chip>
                </v-col>
                <v-col sm="3">
                  <strong>New Quantity:</strong>
                  <v-chip small :color="getNewQuantityColor()" text-color="white">
                    {{ calculateNewQuantity() }}
                  </v-chip>
                </v-col>
                <v-col sm="3">
                  <strong>Action:</strong> {{ getActionTypeLabel() }}
                </v-col>
              </v-row>
              <!-- Warning message for invalid transactions -->
              <v-alert
                v-if="isInvalidTransaction()"
                type="error"
                dense
                class="mt-3"
              >
                <div v-if="form.action_type === 2 && getCompanyStock() <= 0">
                  Cannot remove stock. This variant has no company stock available.
                </div>
                <div v-else-if="form.action_type === 2 && parseInt(form.quantity) > getCompanyStock()">
                  Cannot remove {{ form.quantity }} units. Only {{ getCompanyStock() }} units available in company stock.
                </div>
              </v-alert>
            </v-card-text>
          </v-card>

          <v-divider class="mt-5 mb-5"> </v-divider>
          <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            rounded
            color="primary"
            dark
            @click="save"
          >
            Save Stock Log
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Success Dialog Box -->
      <v-dialog v-model="is_saved" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Stock log created successfully.</h3>
                      <br />
                      <v-btn color="success" @click="continueAdding" class="mr-2">
                        Add More
                      </v-btn>
                      <v-btn color="primary" @click="handleSuccessClose">
                        Done
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";
export default {
  name: "StockLogCreatePage",
  computed: {
    // Show all products with their variant count
    filteredProducts() {
      if (!this.products || this.products.length === 0) return [];

      return this.products.filter(product => {
        // Check if product has any variants
        return product.product_variants && product.product_variants.length > 0;
      }).map(product => {
        const variantCount = product.product_variants.length;
        return {
          ...product,
          name: `${product.name} (${variantCount} variant${variantCount > 1 ? 's' : ''})`
        };
      });
    }
  },
  data() {
    return {
      form: {
        is_active: true,
        variant_id: null,
        quantity: null,
        action_type: null,
        remarks: "",
      },
      validationErrors: {},
      selectedProduct: null,
      products: [],
      productVariants: [],
      selectedVariant: null,
      companyStocks: [],
      companyStockMap: {}, // Map variant_id to company stock data
      actionTypeItems: [
        { value: 1, text: "Add" },
        { value: 2, text: "Remove" },
        // { value: 3, text: "Assign to Distributor" },
        // { value: 4, text: "Assign to Seller" },
        // { value: 5, text: "Assign to Franchise" },
        // { value: 6, text: "Customer Purchase" },
        // { value: 7, text: "Stock Refill" },
      ],
      isLoading: false,
      is_saved: false,
    };
  },
  mounted() {
    this.getCompanyStocks();
    this.getProducts();
  },
  methods: {
    async getCompanyStocks() {
      try {
        const response = await axios.get("/company_stocks");
        this.companyStocks = response.data.data || [];

        // Create a map for quick lookup of company stock by variant_id
        this.companyStockMap = {};
        this.companyStocks.forEach(stock => {
          if (stock.variant_id) {
            this.companyStockMap[stock.variant_id] = stock;
          }
        });
      } catch (error) {
        // Error fetching company stocks
      }
    },
    async getProducts() {
      try {
        const response = await axios.get("/products");
        this.products = response.data.data || [];
      } catch (error) {
        // Error fetching products
      }
    },
    onProductChange() {
      // Reset variant selection
      this.form.variant_id = null;
      this.productVariants = [];
      this.selectedVariant = null;

      // Clear variant error when product changes
      if (this.validationErrors.variant_id) {
        this.$set(this.validationErrors, 'variant_id', null);
      }

      if (!this.selectedProduct) return;

      // Find the selected product
      const product = this.products.find(p => p.id === this.selectedProduct);
      if (product && product.product_variants) {
        // Show all variants, not filtered by company stock
        this.productVariants = product.product_variants;
      }
    },
    onVariantChange() {
      if (this.form.variant_id) {
        this.selectedVariant = this.productVariants.find(v => v.id === this.form.variant_id);
      } else {
        this.selectedVariant = null;
      }
    },
    formatVariantName(variant) {
      let name = [];
      if (variant.color) name.push(`Color: ${variant.color}`);
      if (variant.size) name.push(`Size: ${variant.size}`);
      if (variant.price) name.push(`₹${variant.price}`);

      // Show company stock quantity if available, or indicate no stock
      const stock = this.companyStockMap[variant.id];
      if (stock && stock.quantity > 0) {
        name.push(`Company Stock: ${stock.quantity}`);
      } else {
        name.push(`Company Stock: 0`);
      }

      return name.length > 0 ? name.join(" | ") : "Variant";
    },
    getProductName() {
      if (!this.selectedProduct) return "";
      const product = this.products.find(p => p.id === this.selectedProduct);
      return product ? product.name : "";
    },
    getCompanyStock() {
      if (!this.form.variant_id) return 0;
      const stock = this.companyStockMap[this.form.variant_id];
      return stock ? parseInt(stock.quantity) || 0 : 0;
    },
    calculateNewQuantity() {
      if (!this.form.variant_id || !this.form.quantity || !this.form.action_type) {
        return this.getCompanyStock();
      }

      const currentQty = this.getCompanyStock();
      const changeQty = parseInt(this.form.quantity) || 0;

      // Add or Stock Refill increases quantity
      if ([1, 7].includes(this.form.action_type)) {
        return currentQty + changeQty;
      } else {
        // All other actions decrease quantity
        const newQty = currentQty - changeQty;
        // Show negative as 0 but with warning (actual validation happens on save)
        return newQty < 0 ? 0 : newQty;
      }
    },
    getNewQuantityColor() {
      const newQty = this.calculateNewQuantity();
      if (newQty === 0) return 'red';
      if (newQty < 10) return 'orange';
      return 'green';
    },
    getTransactionPrefix() {
      if ([1, 7].includes(this.form.action_type)) {
        return "+";
      } else {
        return "-";
      }
    },
    getTransactionColor() {
      if ([1, 7].includes(this.form.action_type)) {
        return "green";
      } else {
        return "red";
      }
    },
    getActionTypeLabel() {
      const item = this.actionTypeItems.find(a => a.value === this.form.action_type);
      return item ? item.text : "";
    },
    isInvalidTransaction() {
      if (this.form.action_type !== 2) return false; // Only check for Remove action

      const currentStock = this.getCompanyStock();
      const removeQuantity = parseInt(this.form.quantity) || 0;

      return currentStock <= 0 || removeQuantity > currentStock;
    },
    getPreviewCardClass() {
      return this.isInvalidTransaction() ? 'error-border' : '';
    },
    continueAdding() {
      // Reset form for adding more items
      this.is_saved = false;
      this.form = {
        is_active: true,
        variant_id: null,
        quantity: null,
        action_type: null,
        remarks: "",
      };
      this.selectedProduct = null;
      this.productVariants = [];
      this.selectedVariant = null;
      this.validationErrors = {};

      // Refresh company stocks and products to get updated quantities
      this.getCompanyStocks().then(() => {
        this.getProducts();
      });
    },
    async save() {
      try {
        this.isLoading = true;

        // Clear previous errors
        this.validationErrors = {};

        // Client-side validation
        let hasErrors = false;
        if (!this.selectedProduct) {
          this.$set(this.validationErrors, 'product_id', ['Please select a product']);
          hasErrors = true;
        }
        if (!this.form.variant_id) {
          this.$set(this.validationErrors, 'variant_id', ['Please select a product variant']);
          hasErrors = true;
        }
        if (!this.form.action_type) {
          this.$set(this.validationErrors, 'action_type', ['Please select an action type']);
          hasErrors = true;
        }
        if (!this.form.quantity || this.form.quantity <= 0) {
          this.$set(this.validationErrors, 'quantity', ['Quantity must be greater than 0']);
          hasErrors = true;
        }

        // Check if removing stock - validate against company stock
        if (!hasErrors && this.form.action_type === 2) { // 2 = Remove
          const currentStock = this.getCompanyStock();
          const removeQuantity = parseInt(this.form.quantity) || 0;

          if (currentStock <= 0) {
            this.$set(this.validationErrors, 'quantity', ['Cannot remove stock. No company stock available for this variant.']);
            hasErrors = true;
            // Show alert
            if (this.$toast) {
              this.$toast.error('Cannot remove stock. This variant has no company stock.');
            }
          } else if (removeQuantity > currentStock) {
            this.$set(this.validationErrors, 'quantity', [`Cannot remove ${removeQuantity} units. Only ${currentStock} units available in company stock.`]);
            hasErrors = true;
            // Show alert
            if (this.$toast) {
              this.$toast.error(`Insufficient stock! You can only remove up to ${currentStock} units.`);
            }
          }
        }

        if (hasErrors) {
          this.isLoading = false;
          return;
        }

        await axios.post("/stock_logs", this.form);
        this.isLoading = false;
        this.is_saved = true;
      } catch (e) {
        this.isLoading = false;
        if (e.response && e.response.data && e.response.data.errors) {
          this.validationErrors = e.response.data.errors;
        }
      }
    },
    handleSuccessClose() {
      this.is_saved = false;
      this.$router.push("/stock_logs");
    }
  },
  watch: {
    'form.quantity': function(newVal) {
      // Clear quantity error when valid quantity is entered
      if (newVal && newVal > 0 && this.validationErrors.quantity) {
        this.$set(this.validationErrors, 'quantity', null);
      }
    },
    'form.action_type': function(newVal) {
      // Clear action_type error when selected
      if (newVal && this.validationErrors.action_type) {
        this.$set(this.validationErrors, 'action_type', null);
      }
    }
  }
};
</script>

<style scoped>
.error-border {
  border: 2px solid #ff5252 !important;
}
</style>